import type { ShellSearchToken } from './shell-search.js';

/** The demand list's page size and the largest window a reload re-reads (HS2-X9K02S). */
export const DEMAND_PAGE_SIZE = 100;
export const DEMAND_RELOAD_MAX = 500;

/**
 * The server-side filter of the demand list (HS2-X9K02S): the status filter, and —
 * while the demand list is the visible sidebar — the shell search text and its
 * `is:`, `site:` and `has:questions` tokens, so a search finds demands beyond the
 * loaded pages, and `is:mine` asks for the caller's own assignments (`assignee=me`, which
 * the server resolves from the session; FR-5.20). Every state and site token is sent
 * (repeated `status` and `siteName` parameters, which the server ANDs like the client
 * filter does), so the status counts and paging describe exactly the rows the client
 * shows (HS2-GDXW00). Returns the query string without paging parameters (`''` or
 * `status=…&q=…`), which also keys the list.
 */
export function demandListFilterQuery(input: {
  status: string;
  search: string;
  tokens: readonly ShellSearchToken[];
  searchActive: boolean;
}): string {
  const params = new URLSearchParams();
  const tokens = input.searchActive ? input.tokens : [];
  const valuesOf = (kind: ShellSearchToken['kind']): string[] =>
    tokens.flatMap((token) => (token.kind === kind ? [token.value.trim()] : [])).filter((value) => value !== '');
  const statuses = new Set([...(input.status !== '' ? [input.status] : []), ...valuesOf('state')]);
  for (const status of statuses) params.append('status', status);
  if (input.searchActive) {
    const text = input.search.trim();
    if (text !== '') params.set('q', text);
    for (const site of new Set(valuesOf('site'))) params.append('siteName', site);
    if (tokens.some((token) => token.kind === 'questions')) params.set('questions', '1');
    if (tokens.some((token) => token.kind === 'mine')) params.set('assignee', 'me');
  }
  return params.toString();
}

/** The full list request query: the filter plus the page size and an optional cursor. */
export function demandListRequestQuery(filter: string, limit: number, cursor?: string): string {
  const params = new URLSearchParams(filter);
  params.set('limit', String(limit));
  if (cursor !== undefined) params.set('cursor', cursor);
  return `?${params.toString()}`;
}
